import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Dialog } from "./ui-kit/dialog";
import "../../src/ui/dialog.css";

type Piece = { src: string; title: string; caption: string };

/**
 * An Art wall's full view: pressing a piece opens it large with its title and caption, and the
 * arrows (buttons or keys) step through the wall. The pieces are read from the server-drawn cards'
 * attributes, so the wall needs no data of its own in the browser.
 */
export function PictureFullView({ id }: { id: string }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [index, setIndex] = useState<number | null>(null);
  useEffect(() => {
    const wall = anchor.current?.closest<HTMLElement>("[data-page-full-view]");
    if (!wall) return;
    const cards = () => [...wall.querySelectorAll<HTMLElement>(":scope > [data-full-view-src]")];
    const open = (event: MouseEvent) => {
      const button = (event.target as Element | null)?.closest("[data-full-view-open]");
      const card = button?.closest<HTMLElement>("[data-full-view-src]");
      if (!card || card.parentElement !== wall) return;
      const all = cards();
      setPieces(
        all.map((entry) => ({
          src: entry.dataset.fullViewSrc ?? "",
          title: entry.dataset.fullViewTitle ?? "",
          caption: entry.dataset.fullViewCaption ?? "",
        })),
      );
      setIndex(all.indexOf(card));
      (document.getElementById(id) as HTMLDialogElement | null)?.showModal();
    };
    wall.addEventListener("click", open);
    return () => wall.removeEventListener("click", open);
  }, [id]);
  const piece = index === null ? undefined : pieces[index];
  const step = (by: number) =>
    setIndex((at) => (at === null ? at : (at + by + pieces.length) % pieces.length));
  const keys = (event: KeyboardEvent) => {
    if (event.key === "ArrowLeft") step(-1);
    if (event.key === "ArrowRight") step(1);
  };
  return (
    <>
      <span ref={anchor} hidden />
      <Dialog
        id={id}
        title={piece?.title || "Picture"}
        size="lg"
        className="drate-page-full-view"
        onClose={() => setIndex(null)}
      >
        {/* The dialog takes the arrow keys while it is open. */}
        <div className="drate-page-full-view-body" onKeyDown={keys}>
          {piece && (
            <figure>
              <img src={piece.src} alt={piece.title} decoding="async" />
              {piece.caption && <figcaption>{piece.caption}</figcaption>}
            </figure>
          )}
          {pieces.length > 1 && (
            <div className="drate-page-full-view-steps">
              <button
                type="button"
                className="button button-outline button-small"
                onClick={() => step(-1)}
              >
                Previous
              </button>
              <span>
                {(index ?? 0) + 1} of {pieces.length}
              </span>
              <button
                type="button"
                className="button button-outline button-small"
                onClick={() => step(1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
